import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { format, parseISO, isPast } from 'date-fns';
import { createPortal } from 'react-dom';
import { Lock } from 'lucide-react';
import { toast } from 'sonner';
import { ACTION_TYPE_CONFIG, isStepCompleted, isStepNotNeeded, getPhaseItems } from './journey-constants';
import StepDetailPanel from './StepDetailPanel';
import '@/styles/journey.css';
import { buildStepUrl, stepErrorToast, completeStepRequest } from './stepActions';

/**
 * JourneySteps
 * --------------------------------
 * The "All Steps by Phase" horizontal strip. DP41 commit 1 lifted the detail
 * panel and its five dialogs out into StepDetailPanel; selection, the keyboard
 * navigation and the Chad cold-links stay here, unchanged.
 *
 * Rendered both inline on ProjectHubFull (project page, no header/activity) and
 * inside the full ProjectJourney page (with header search + Recent Activity).
 *
 * Props:
 *   project                 — current project (for API URLs + smart-action routes)
 *   phases                  — phase[] with steps[] (source of truth)
 *   onUpdate                — called after a step mutation to refresh parent project
 *   onActivityRefresh       — optional: called after a mutation to refresh Recent Activity
 *   onSelectedStepChange    — optional: notified when selectedStep changes (for parent breadcrumb)
 *   darkMode                — theme toggle
 *   teamMembers             — available assignees for the Schedule dialog
 *   searchQuery             — controlled search filter (parent owns the input)
 *   enableKeyboardNav       — when true, attach window keydown for Arrow/Enter/Escape
 */
export default function JourneySteps({
  project,
  phases,
  onUpdate,
  onActivityRefresh = () => {},
  onSelectedStepChange = () => {},
  darkMode = false,
  teamMembers = [],
  searchQuery = '',
  enableKeyboardNav = true,
  compact = false,
  // Container-agnostic rendering (Onboarding Arc Stage B): onboarding
  // journeys reuse this strip with their own step endpoint and a slimmer
  // detail panel (no schedule/nudge/lock/checklist/edit — those are
  // project-container concepts).
  stepUrlBuilder = null,
  variant = 'project',
  // DP41 — the scroller is one of two viewers of the same data. When the
  // caller draws the step panel itself (My Desk B.9 / project page B.2) the
  // strip reports its selection outward instead of drawing a second panel.
  externalPanel = false,
  panelHost = null,
  selectedUid = undefined,
  onStepSelected = null,
  initialStepId = null,
}) {
  const scrollContainerRef = useRef(null);

  // Block-local state
  const [selectedStep, setSelectedStep] = useState(null);
  const [hoveredStep, setHoveredStep] = useState(null);
  const [cancelActivityLog, setCancelActivityLog] = useState(false);

  // Notify parent of selected-step changes (for breadcrumb on the full Journey page).
  // Use a ref so prop identity churn doesn't refire.
  const onSelectedStepChangeRef = useRef(onSelectedStepChange);
  useEffect(() => { onSelectedStepChangeRef.current = onSelectedStepChange; }, [onSelectedStepChange]);
  useEffect(() => { onSelectedStepChangeRef.current(selectedStep); }, [selectedStep]);

  // Flatten phases → step list with phase metadata.
  // `uid` (phase.id + step.id) is the IDENTITY key for hover/selection/up-next:
  // legacy projects synthesize tasks[] ids as 'task-<idx>' which COLLIDE across
  // phases — matching by bare step.id made hovering one card light up the same
  // index in every phase, and stamped "UP NEXT" per-phase instead of per-project.
  const allSteps = useMemo(() => {
    const steps = [];
    phases?.forEach((phase, phaseIdx) => {
      getPhaseItems(phase).forEach((step, stepIdx) => {
        steps.push({
          ...step,
          // DP83 — legacy phases can have NO id at all, which made every uid
          // read `undefined:task-0` and collide across phases (2,112 duplicate-key
          // errors on one Project Desk load). The phase index is the fallback.
          uid: `${phase.id || `phase-${phaseIdx}`}:${step.id}`,
          phase,
          phaseIndex: phaseIdx,
          stepIndex: stepIdx,
        });
      });
    });
    return steps;
  }, [phases]);

  // Filter steps by parent-controlled search query
  const filteredSteps = useMemo(() => {
    if (!searchQuery.trim()) return allSteps;
    const query = searchQuery.toLowerCase();
    return allSteps.filter(step =>
      step.name?.toLowerCase().includes(query) ||
      step.description?.toLowerCase().includes(query) ||
      step.phase?.name?.toLowerCase().includes(query)
    );
  }, [allSteps, searchQuery]);

  // Current selected step index (for keyboard navigation)
  const selectedStepIndex = useMemo(() => {
    if (!selectedStep) return -1;
    return filteredSteps.findIndex(s => s.uid === selectedStep.uid);
  }, [selectedStep, filteredSteps]);

  // Horizontal wheel scroll
  const handleWheel = useCallback((e) => {
    if (scrollContainerRef.current) {
      e.preventDefault();
      e.stopPropagation();
      scrollContainerRef.current.scrollLeft += e.deltaY * 2;
    }
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);

  // Auto-scroll the strip to the first un-completed step on mount (and
  // whenever the phases/steps list materially changes — e.g. a step gets
  // completed and we want the cursor to advance). Prevents the strip from
  // always resetting to phase 1 when the user re-opens it on a project
  // that's deep into Construction Admin.
  const scrollStripTo = (card, behavior = 'auto', align = 'start') => {
    const container = scrollContainerRef.current;
    if (!card || !container) return;
    const c = container.getBoundingClientRect();
    const r = card.getBoundingClientRect();
    const delta = align === 'center'
      ? (r.left + r.width / 2) - (c.left + c.width / 2)
      : r.left - c.left - 16;
    container.scrollBy({ left: delta, top: 0, behavior });
  };
  const firstIncompleteUid = useMemo(() => (allSteps.find(
    s => !s.completed && s.status !== 'completed' && s.status !== 'skipped'
  ) || allSteps[allSteps.length - 1])?.uid, [allSteps]);  // fall back to last step if all done
  const [hostReady, setHostReady] = useState(false);
  useEffect(() => { if (panelHost) setHostReady(true); }, [panelHost]);
  const hasAutoScrolledRef = useRef(false);
  useEffect(() => {
    if (hasAutoScrolledRef.current) return;
    const container = scrollContainerRef.current;
    if (!container || !allSteps.length) return;
    const firstIncomplete = allSteps.find((st) => st.uid === firstIncompleteUid);
    if (!firstIncomplete) return;
    // Defer one frame so the DOM has actually rendered the cards.
    const raf = requestAnimationFrame(() => {
      const card = container.querySelector(
        `[data-testid="step-card-${firstIncomplete.uid}"]`
      );
      if (card) {
        scrollStripTo(card, 'auto', 'start');
        hasAutoScrolledRef.current = true;
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [allSteps, firstIncompleteUid]);

  // PS-3b — arriving with ?step= selects that step once per value.
  const appliedStepRef = useRef(null);
  useEffect(() => {
    if (!initialStepId || appliedStepRef.current === initialStepId || !allSteps.length) return undefined;
    const s = allSteps.find((st) => st.id === initialStepId || st.uid === initialStepId);
    if (!s) return undefined;
    setSelectedStep(s);
    onStepSelected?.(s);
    let tries = 0;
    let raf;
    const land = () => {
      const card = scrollContainerRef.current?.querySelector(`[data-testid="step-card-${s.uid}"]`);
      if (card) { scrollStripTo(card, 'instant', 'center'); appliedStepRef.current = initialStepId; }
      else if (tries++ < 30) raf = requestAnimationFrame(land);
    };
    raf = requestAnimationFrame(land);
    return () => cancelAnimationFrame(raf);
  }, [allSteps, initialStepId]); // eslint-disable-line react-hooks/exhaustive-deps

  const completeStepFromKeyboard = async (step, skipActivityLog = false) => {
    try {
      await completeStepRequest(buildStepUrl(project, step, stepUrlBuilder), skipActivityLog);
      toast.success('Step completed!');
      setSelectedStep(null);
      if (onUpdate) onUpdate();
      onActivityRefresh();
    } catch (error) {
      stepErrorToast(error, 'Failed to complete step');
    }
  };

  // Keyboard navigation — gated by prop so two simultaneous instances don't both react.
  // Bundled fix (Risk #1): scope card lookup to scrollContainerRef so the listener
  // scrolls THIS instance's strip, not whichever step-card-${id} happens to appear
  // first in the global DOM.
  useEffect(() => {
    if (!enableKeyboardNav) return;
    const handleKeyDown = (e) => {
      if (!filteredSteps.length) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      const scrollToCardInThisInstance = (stepUid) => {
        const card = scrollContainerRef.current?.querySelector(`[data-testid="step-card-${stepUid}"]`);
        scrollStripTo(card, 'smooth', 'center');
      };

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        const nextIndex = selectedStepIndex < filteredSteps.length - 1 ? selectedStepIndex + 1 : 0;
        setSelectedStep(filteredSteps[nextIndex]);
        scrollToCardInThisInstance(filteredSteps[nextIndex].uid);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        const prevIndex = selectedStepIndex > 0 ? selectedStepIndex - 1 : filteredSteps.length - 1;
        setSelectedStep(filteredSteps[prevIndex]);
        scrollToCardInThisInstance(filteredSteps[prevIndex].uid);
      } else if (e.key === 'Enter' && selectedStep) {
        if (!selectedStep.completed && selectedStep.status !== 'completed') {
          completeStepFromKeyboard(selectedStep, cancelActivityLog);
        }
      } else if (e.key === 'Escape') {
        setSelectedStep(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableKeyboardNav, selectedStepIndex, filteredSteps, selectedStep, cancelActivityLog]);

  // Cold linking (Chad text → strip): respond to events dispatched by the
  // chat surface when the user hovers/clicks a [[step:...]] link in Chad's
  // reply. USER-TRIGGERED ONLY — nothing here moves without a hover/click.
  // Unknown step ids are ignored (graceful plain-text degrade upstream).
  useEffect(() => {
    // Match by bare step id (rich steps, globally unique uuids) OR by uid
    // (phase-qualified <phase_id>:task-<idx> ids the backend emits for
    // legacy rows, whose bare ids collide across phases).
    const findStep = (stepId) => allSteps.find((s) => s.id === stepId || s.uid === stepId);
    const scrollToCard = (uid) => {
      const card = scrollContainerRef.current?.querySelector(`[data-testid="step-card-${uid}"]`);
      scrollStripTo(card, 'smooth', 'center');
    };
    const onHover = (e) => {
      const { stepId, hovering } = e.detail || {};
      const step = stepId ? findStep(stepId) : null;
      if (!step) return;
      setHoveredStep(hovering ? step.uid : null);
      if (hovering) scrollToCard(step.uid);
    };
    const onOpen = (e) => {
      const step = findStep(e.detail?.stepId);
      if (!step) return;
      setSelectedStep(step);
      scrollToCard(step.uid);
    };
    window.addEventListener('keymade:journey-step-hover', onHover);
    window.addEventListener('keymade:journey-step-open', onOpen);
    return () => {
      window.removeEventListener('keymade:journey-step-hover', onHover);
      window.removeEventListener('keymade:journey-step-open', onOpen);
    };
  }, [allSteps]);

  // DP41 — a controlled selection, when the caller owns it.
  useEffect(() => {
    if (selectedUid === undefined) return;
    setSelectedStep(allSteps.find((s) => s.uid === selectedUid) || null);
  }, [selectedUid, allSteps]);

  // Group steps by phase for display
  const stepsByPhase = useMemo(() => {
    const groups = {};
    filteredSteps.forEach(step => {
      const phaseId = step.phase.id;
      if (!groups[phaseId]) {
        groups[phaseId] = {
          phase: step.phase,
          phaseIndex: step.phaseIndex,
          steps: [],
        };
      }
      groups[phaseId].steps.push(step);
    });
    return Object.values(groups).sort((a, b) => a.phaseIndex - b.phaseIndex);
  }, [filteredSteps]);

  // Render step thumbnail card
  const renderStepCard = (step, isHovered, isSelected) => {
    const config = ACTION_TYPE_CONFIG[step.action_type] || ACTION_TYPE_CONFIG.file;
    const ActionIcon = config.icon;
    const isCompleted = step.completed || step.status === 'completed';
    const isSkipped = step.status === 'skipped';
    const isNow = step.uid === firstIncompleteUid;
    const checks = step.checklist?.filter(c => c.completed).length || 0;
    return (
      <div
        key={step.uid}
        className={`kjs-card${isCompleted ? ' is-done' : ''}${isSkipped ? ' is-skipped' : ''}${isSelected ? ' is-sel' : ''}${isHovered ? ' is-hover' : ''}${isNow ? ' is-now' : ''}`}
        onMouseEnter={() => setHoveredStep(step.uid)}
        onMouseLeave={() => setHoveredStep(null)}
        onClick={() => {
          const next = selectedStep?.uid === step.uid ? null : step;
          setSelectedStep(next);
          onStepSelected?.(next);
        }}
        data-testid={`step-card-${step.uid}`}
        data-step-id={step.id}
      >
        <div className="kjs-hd"><span>{step.phase.name}</span><span>{isCompleted ? '✓ done' : isSkipped ? 'not needed' : isNow ? 'now' : step.requires_client_reply ? 'client' : ''}</span></div>
        <div className="kjs-bd">
          <ActionIcon className="kjs-ic" aria-hidden="true" />
          <span className="kjs-name">{step.name}</span>
          {step.is_locked && <Lock className="kjs-lock" data-testid={`step-locked-${step.id}`} />}
          {step.is_critical && <span className="kjs-star" title="critical">★</span>}
        </div>
        <div className="kjs-ft">
          {step.is_optional && !isCompleted && !isSkipped && <span className="kjs-tag">optional</span>}
          {step.checklist?.length > 0 && <span className="kjs-tag">{checks}/{step.checklist.length} checks</span>}
          {step.due_date && !isCompleted && !isSkipped && <span className={`kjs-tag ${isPast(parseISO(step.due_date)) ? 'late' : 'wait'}`}>due {format(parseISO(step.due_date), 'd MMM')}</span>}
          {isCompleted && step.completed_at && <span className="kjs-m">{format(parseISO(step.completed_at), 'd MMM')}{step.completed_by ? ` · ${step.completed_by}` : ''}</span>}
          {isSkipped && step.skip_reason && <span className="kjs-m" data-testid={`skip-reason-${step.id}`}>“{step.skip_reason}”</span>}
        </div>
      </div>
    );
  };

  const panel = (
    <StepDetailPanel
      project={project}
      step={selectedStep}
      allSteps={allSteps}
      darkMode={darkMode}
      compact={compact}
      teamMembers={teamMembers}
      variant={variant}
      stepUrlBuilder={stepUrlBuilder}
      onUpdate={onUpdate}
      onActivityRefresh={onActivityRefresh}
      onClose={() => setSelectedStep(null)}
      onStepPatch={(patch) => setSelectedStep(prev => (prev ? { ...prev, ...patch } : prev))}
      cancelActivityLog={cancelActivityLog}
      onCancelActivityLogChange={setCancelActivityLog}
    />
  );

  return (
    <div data-testid="journey-steps" className={`kjs${compact ? ' is-compact' : ''}`}>
      {searchQuery && <div className="kjs-m">Search results · {filteredSteps.length}</div>}
      <div ref={scrollContainerRef} className="kjs-strip" style={{ scrollbarWidth: 'thin' }}>
        {stepsByPhase.map(({ phase, phaseIndex, steps }) => (
          <div key={phase.id || `phase-${phaseIndex}`} className="kjs-group">
            <div className="kjs-ghead">
              <span className="kjs-gno">{phaseIndex + 1}</span><b>{phase.name}</b>
              <span className="kjs-m">{steps.filter(isStepCompleted).length}/{Math.max(steps.length - steps.filter(isStepNotNeeded).length, 0)}</span>
            </div>
            <div className="kjs-cards">
              {steps.map(step => renderStepCard(step, hoveredStep === step.uid, selectedStep?.uid === step.uid))}
            </div>
          </div>
        ))}
        {filteredSteps.length === 0 && <div className="kjs-empty">No steps match your search.</div>}
      </div>

      {/* Detail Panel — DP41: the same component My Desk uses. When the caller
          draws it (B.9 / B.2), the strip does not draw a second one; with
          panelHost (PS-2f2) it draws into the caller's JP.5 sheet. */}
      {!externalPanel && (panelHost ? (hostReady && panelHost.current ? createPortal(panel, panelHost.current) : null) : panel)}
    </div>
  );
}
